import type { EchoflicksVideoSession } from "../types";

// ---------------------------------------------------------------------------
// Pure helpers that turn the projected GPT dog census (session.dog_census_*
// fields, from the orchestrator's single observe-only GPT vision call over the
// uploaded photos) into a PM-readable summary.
//
// Key framing: the census is OBSERVE-ONLY. It never changes the echo — it
// exists so we can compare what one GPT call sees against the identity
// pipeline's answer, echo by echo, before trusting it with anything.
// ---------------------------------------------------------------------------

export type DogCensusStatus = "observed" | "unavailable";

export type DogCensusDog = {
  breed: string; // never empty — falls back to "unknown"
  confidence: string | null; // high|medium|low (permissive)
  photoCount: number | null;
};

export type DogCensusImageCounts = {
  analyzed: number | null;
  available: number | null;
  failed: number | null;
};

export type DogCensusSummary = {
  status: DogCensusStatus;
  dogCount: number | null;
  countConfidence: string | null; // high|medium|low (permissive)
  dogs: DogCensusDog[];
  /** Bounded operator English from the model, e.g. "two goldens distinguished by collar color". */
  reason: string | null;
  modelId: string | null;
  unavailableReason: string | null;
  imageCounts: DogCensusImageCounts;
  observedAt: string | null;
  /** The identity lane's subject_count, read defensively for the comparison line. */
  identitySubjectCount: number | null;
  /** Only set when both counts are finite numbers — the whole point of the pilot. */
  identityAgreement: "match" | "differs" | null;
};

function toFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function summarizeDogCensus(
  session: EchoflicksVideoSession | null | undefined,
): DogCensusSummary | null {
  if (!session) return null;
  const status = session.dog_census_status;
  // No census projected (or an unknown status) → render nothing. The card
  // self-hides everywhere the lane has not run.
  if (status !== "observed" && status !== "unavailable") return null;

  const dogs: DogCensusDog[] = (session.dog_census_dogs ?? []).map((dog) => ({
    breed: toTrimmedString(dog?.breed) ?? "unknown",
    confidence: toTrimmedString(dog?.confidence),
    photoCount: toFiniteNumber(dog?.photo_count),
  }));

  const counts = session.dog_census_image_counts ?? null;
  const dogCount = toFiniteNumber(session.dog_census_dog_count);
  const identitySubjectCount = toFiniteNumber(
    (session.identity_summary as { subject_count?: unknown } | null | undefined)
      ?.subject_count,
  );
  const identityAgreement =
    dogCount !== null && identitySubjectCount !== null
      ? dogCount === identitySubjectCount
        ? "match"
        : "differs"
      : null;

  return {
    status,
    dogCount,
    countConfidence: toTrimmedString(session.dog_census_count_confidence),
    dogs,
    reason: toTrimmedString(session.dog_census_reason),
    modelId: toTrimmedString(session.dog_census_model_id),
    unavailableReason: toTrimmedString(session.dog_census_unavailable_reason),
    imageCounts: {
      analyzed: toFiniteNumber(counts?.analyzed),
      available: toFiniteNumber(counts?.available),
      failed: toFiniteNumber(counts?.failed),
    },
    observedAt: toTrimmedString(session.dog_census_observed_at),
    identitySubjectCount,
    identityAgreement,
  };
}

/** Friendly label for a count/breed confidence; falls back to the raw value. */
export function censusConfidenceLabel(confidence?: string | null): string {
  switch ((confidence ?? "").toLowerCase()) {
    case "high":
      return "high confidence";
    case "medium":
      return "medium confidence";
    case "low":
      return "low confidence";
    default:
      return confidence ?? "unknown confidence";
  }
}

const UNAVAILABLE_LABELS: Record<string, string> = {
  timeout: "timed out",
  api_error: "provider error",
  schema_mismatch: "malformed model output",
  image_loads_failed: "photos could not be loaded",
};

/** Operator English for why the census did not run; falls back to "did not run". */
export function censusUnavailableLabel(reason?: string | null): string {
  if (!reason) return "did not run";
  return UNAVAILABLE_LABELS[reason] ?? "did not run";
}
